import $ from '../jquery';
import EventEmitter from './eventEmitter';

/**
 * This class handles keyboard events
 */
export default class KeyBinder extends EventEmitter {
    KEY_CODE_ESC = 27;
    KEY_CODE_ALT = 18;
    KEY_CODE_SPACE = 32;
    KEY_CODE_LEFT = 37;
    KEY_CODE_UP = 38;
    KEY_CODE_RIGHT = 39;
    KEY_CODE_DOWN = 40;
    KEY_CODE_DEL = 46;
    KEY_CODE_BACKSPACE = 8;
    KEY_CODE_PLUS = 187;
    KEY_CODE_PLUS_NUMPAD = 107;
    KEY_CODE_PLUS_FIREFOX = 171;
    KEY_CODE_MINUS = 189;
    KEY_CODE_MINUS_FIREFOX = 173;
    KEY_CODE_MINUS_NUMPAD = 109;
    KEY_CODE_POINT = 190;
    KEY_CODE_HASH = 220;
    KEY_CODE_0 = 48;
    KEY_CODE_1 = 49;
    KEY_CODE_2 = 50;
    KEY_CODE_3 = 51;
    KEY_CODE_4 = 52;
    KEY_CODE_5 = 53;
    KEY_CODE_6 = 54;
    KEY_CODE_7 = 55;
    KEY_CODE_8 = 56;
    KEY_CODE_9 = 57;
    KEY_CODE_A = 65;
    KEY_CODE_C = 67;
    KEY_CODE_D = 68;
    KEY_CODE_F = 70;
    KEY_CODE_G = 71;
    KEY_CODE_H = 72;
    KEY_CODE_I = 73;
    KEY_CODE_K = 75;
    KEY_CODE_L = 76;
    KEY_CODE_M = 77;
    KEY_CODE_N = 78;
    KEY_CODE_R = 82;
    KEY_CODE_S = 83;
    KEY_CODE_T = 84;
    KEY_CODE_V = 86;
    KEY_CODE_X = 88;
    KEY_CODE_Z = 90;
    KEY_CODE_NUM_0 = 96;
    KEY_CODE_NUM_1 = 97;
    KEY_CODE_NUM_2 = 98;
    KEY_CODE_NUM_3 = 99;
    KEY_CODE_NUM_4 = 100;
    KEY_CODE_NUM_5 = 101;
    KEY_CODE_NUM_6 = 102;
    KEY_CODE_NUM_7 = 103;
    KEY_CODE_NUM_8 = 104;
    KEY_CODE_NUM_9 = 105;
    KEY_CODE_OPEN_BRACKET = 219;
    KEY_CODE_CLOSE_BRACKET = 221;

    mappings = new Map();
    element = null;
    _isMuted = false;

    constructor(element) {
        super();

        this.element = $(element);

        this
            .getMappings()
            .filter(mapping => !!mapping.callback)
            .forEach((mapping) => {
                this._addMapping({
                    ...mapping,
                    event: mapping.event || 'keydown',
                    shift: !!mapping.shift,
                    meta: !!mapping.meta,
                    alt: !!mapping.alt,
                });
            });

        this._initKeyBindings();
    }

    mute() {
        this._isMuted = true;
    }

    unmute() {
        this._isMuted = false;
    }

    _buildMapKey(event, keyCode) {
        return `${event}#${keyCode}`;
    }

    _addMapping(mapping) {
        mapping.keyCodes.forEach((keyCode) => {
            const mapKey = this._buildMapKey(mapping.event, keyCode);

            if (!this.mappings.has(mapKey)) {
                this.mappings.set(mapKey, new Set());
            }

            this.mappings.get(mapKey).add(mapping);
        });
    }

    /**
     * @param {string} event
     * @param {string} keyCode
     * @return {Set}
     * @private
     */
    _getMapping(event, keyCode) {
        const key = this._buildMapKey(event, keyCode);
        return this.mappings.get(key);
    }

    /**
     * @param {string} event
     * @param {string} keyCode
     * @return {boolean}
     * @private
     */
    _hasMapping(event, keyCode) {
        const key = this._buildMapKey(event, keyCode);
        return this.mappings.has(key);
    }

    getMappings() {
        throw new Error('getMappings() not implemented in base keybinder');
    }

    _initKeyBindings() {
        this.element.on('keydown keyup', (e) => {
            if (!this._hasMapping(e.type, e.keyCode)) return;

            const isShift = e.shiftKey;
            const isMeta = (e.ctrlKey || e.metaKey);
            const isAlt = e.altKey;

            this._getMapping(e.type, e.keyCode)
                .forEach((mapping) => {
                    if (this.specialKeyMatches(isShift, isMeta, isAlt, mapping)) {
                        if (!this._isMuted) mapping.callback.call(this, e);

                        e.preventDefault();
                        e.stopPropagation();
                    }
                });
        });
    }

    specialKeyMatches(isShift, isMeta, isAlt, mapping) {
        if (mapping.shift && !isShift) {
            return false;
        }

        if (!mapping.shift && isShift) {
            return false;
        }

        if (mapping.meta && !isMeta) {
            return false;
        }

        if (!mapping.meta && isMeta) {
            return false;
        }

        if (mapping.alt && !isAlt) {
            return false;
        }

        if (!mapping.alt && isAlt) {
            return false;
        }

        return true;
    }
}
